import { CanonicalBusinessEvent } from "../types/cbes";

export interface BundleRevenueTimelineEntry {
  date: string;
  productId: string;
  eventType: "prepaid" | "fulfilled" | "refunded" | "pending";
  amount: number;
  currencyCode: string;
  deferredBalance: number;
}

export interface BundleRevenueSummary {
  initialDeferredRevenue: number;
  currentDeferredBalance: number;
  fulfilledCount: number;
  totalCount: number;
  completionPercentage: number;
  currencyCode: string;
}

interface BundleEvent {
  usecaseName: string;
  transactionDate: string;
  amount: number;
  currencyCode: string;
  productIdentifier: string;
  version: number;
}

const EVENT_SORT_ORDER: Record<string, number> = { prepaid: 0, fulfilled: 1, refunded: 2, pending: 3 };

const extractProductIdentifier = (idempotenceId: string): string => {
  const match = idempotenceId.match(/amzn1\.dv\.(gti|offer)\.[a-f0-9-]+/i);
  return match ? match[0] : idempotenceId;
};

const sumSimpleCBERecords = (
  records: any,
  entryType: "CREDIT" | "DEBIT"
): { amount: number; currencyCode: string } => {
  let amount = 0;
  let currencyCode = "USD";
  if (!records) return { amount, currencyCode };
  const arr = Array.isArray(records) ? records : Object.values(records);
  for (const r of arr) {
    const content = r.content || r;
    if (r["@type"] === "SimpleCBERecord" && content.entryType === entryType) {
      const fa = content.functionalAmount;
      if (fa) {
        amount += parseFloat(fa.value || "0");
        currencyCode = fa.currencyCode || "USD";
      }
    }
  }
  return { amount: Math.round(amount * 100) / 100, currencyCode };
};

const deduplicateCBEs = (cbes: CanonicalBusinessEvent[]): CanonicalBusinessEvent[] => {
  const map = new Map<string, CanonicalBusinessEvent>();
  for (const cbe of cbes) {
    const key = cbe.idempotenceId;
    const existing = map.get(key);
    if (!existing || (cbe.idempotenceVersion || 0) > (existing.idempotenceVersion || 0)) {
      map.set(key, cbe);
    }
  }
  return Array.from(map.values());
};

const extractBundleEvents = (
  cbes: CanonicalBusinessEvent[],
  usecaseName: string,
  entryType: "CREDIT" | "DEBIT"
): BundleEvent[] => {
  const filtered = cbes.filter((c) => c.usecaseName === usecaseName);
  const deduped = deduplicateCBEs(filtered);
  return deduped.map((cbe) => {
    const { amount, currencyCode } = sumSimpleCBERecords(cbe.records, entryType);
    return {
      usecaseName,
      transactionDate: cbe.transactionDate,
      amount,
      currencyCode,
      productIdentifier: extractProductIdentifier(cbe.idempotenceId),
      version: cbe.idempotenceVersion || 0,
    };
  });
};

export const isBundleOrder = (cbe: CanonicalBusinessEvent): boolean => {
  const attrs = cbe.internalAttributes;
  return (
    attrs?.IS_BUNDLE === "true" ||
    attrs?.IS_BUNDLE_IMMEDIATE === "true" ||
    attrs?.IS_SEASON_PASS_BUNDLE === "true" ||
    attrs?.IS_PREORDER_BUNDLE === "true"
  );
};

export const hasBundleOrder = (cbes: CanonicalBusinessEvent[]): boolean => cbes.some(isBundleOrder);

export const hasPrepaidEvents = (cbes: CanonicalBusinessEvent[]): boolean =>
  cbes.some((c) => c.usecaseName === "PrepaidChargeSuccess" && isBundleOrder(c));

export const generateBundleRevenueTimeline = (
  cbes: CanonicalBusinessEvent[],
  dbdProducts?: { [key: string]: any }
): BundleRevenueTimelineEntry[] => {
  const prepaidEvents = extractBundleEvents(cbes, "PrepaidChargeSuccess", "CREDIT");
  const fulfillmentEvents = extractBundleEvents(cbes, "PaidFulfillment", "CREDIT");
  const refundEvents = extractBundleEvents(cbes, "Refunded", "DEBIT");

  if (!prepaidEvents.length && !fulfillmentEvents.length) return [];

  const fulfilledProductIds = new Set(fulfillmentEvents.map((e) => e.productIdentifier));

  // Build raw timeline entries (balance computed after sorting)
  type RawEntry = { date: string; productId: string; eventType: "prepaid" | "fulfilled" | "refunded"; amount: number; currencyCode: string };
  const raw: RawEntry[] = [];

  for (const e of prepaidEvents) {
    raw.push({ date: e.transactionDate, productId: e.productIdentifier, eventType: "prepaid", amount: e.amount, currencyCode: e.currencyCode });
  }
  for (const e of fulfillmentEvents) {
    raw.push({ date: e.transactionDate, productId: e.productIdentifier, eventType: "fulfilled", amount: e.amount, currencyCode: e.currencyCode });
  }
  for (const e of refundEvents) {
    if (!fulfilledProductIds.has(e.productIdentifier)) {
      raw.push({ date: e.transactionDate, productId: e.productIdentifier, eventType: "refunded", amount: e.amount, currencyCode: e.currencyCode });
    }
  }

  // Sort: chronologically, then prepaid → fulfilled → refunded
  raw.sort((a, b) => {
    const timeDiff = new Date(a.date).getTime() - new Date(b.date).getTime();
    if (timeDiff !== 0) return timeDiff;
    return (EVENT_SORT_ORDER[a.eventType] || 0) - (EVENT_SORT_ORDER[b.eventType] || 0);
  });

  // Compute running deferred balance
  let balance = 0;
  const currencyCode = raw[0]?.currencyCode || "USD";
  const timeline: BundleRevenueTimelineEntry[] = raw.map((entry) => {
    const delta = entry.eventType === "prepaid" ? entry.amount : -entry.amount;
    balance = Math.max(0, Math.round((balance + delta) * 100) / 100);
    return {
      date: entry.date,
      productId: entry.productId,
      eventType: entry.eventType,
      amount: entry.eventType === "prepaid" ? entry.amount : -entry.amount,
      currencyCode: entry.currencyCode,
      deferredBalance: balance,
    };
  });

  // Pending items from dbdProducts
  if (dbdProducts) {
    const accountedIds = new Set([...fulfilledProductIds, ...refundEvents.filter((e) => !fulfilledProductIds.has(e.productIdentifier)).map((e) => e.productIdentifier)]);
    for (const productKey of Object.keys(dbdProducts)) {
      const normalized = extractProductIdentifier(productKey);
      if (!accountedIds.has(normalized) && !fulfilledProductIds.has(normalized)) {
        timeline.push({
          date: "",
          productId: productKey,
          eventType: "pending",
          amount: 0,
          currencyCode,
          deferredBalance: balance,
        });
      }
    }
  }

  return timeline;
};

export const calculateBundleRevenueSummary = (
  cbes: CanonicalBusinessEvent[],
  dbdProductCount?: number
): BundleRevenueSummary | null => {
  const timeline = generateBundleRevenueTimeline(cbes);
  if (!timeline.length) return null;

  const prepaidEntries = timeline.filter((e) => e.eventType === "prepaid");
  const initialDeferredRevenue = Math.round(prepaidEntries.reduce((sum, e) => sum + e.amount, 0) * 100) / 100;
  const currentDeferredBalance = timeline[timeline.length - 1].deferredBalance;
  const fulfilledCount = new Set(timeline.filter((e) => e.eventType === "fulfilled").map((e) => e.productId)).size;
  const totalCount = dbdProductCount || fulfilledCount;
  const currencyCode = timeline[0]?.currencyCode || "USD";

  return {
    initialDeferredRevenue,
    currentDeferredBalance,
    fulfilledCount,
    totalCount,
    completionPercentage: totalCount > 0 ? Math.round((fulfilledCount / totalCount) * 100) : 0,
    currencyCode,
  };
};
